import React from 'react';
import {Box, FormHelperText} from '@mui/material';
import OtpInput from "react-otp-input";
import {useTranslation} from "react-i18next";
import {CustomStyleFormHelperText, CustomStyleOtpContainer, CustomStyleOtpContainerRequired} from "./Styles";
import CustomInputLabel from "./CustomInputLabel";

/**
 *
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description CustomOtpInput component for OTP (One Time Password) input with dynamic number of fields,
 * optional validation, and customized behavior for each input field.
 * @param {String} id: Card identifier (optional, default is 'otp_coi_field').
 * @param {String} value: The value to be displayed in the OTP input fields (required).
 * @param {Number} numInputs: Number of OTP input fields (optional, default is 6).
 * @param {Boolean} isInputNum: Whether the input should accept numeric values (optional, default is true).
 * @param {Boolean} onlyNumbers: Whether the input should only allow numeric characters (optional, default is false).
 * @param {Boolean} required: Whether the OTP input is required (optional, default is false).
 * @param {Function} onChange: Callback function triggered when the value of the input changes (optional).
 * @param {Boolean} emptyFields: Whether to show empty fields as part of the input (optional, default is false).
 * @param {Boolean} isValid: Whether the OTP input is valid (optional, default is true).
 * @returns {JSX.Element}: Rendered JSX element for the OTP input fields.
 */
const CustomOtpInput = ({
                            value,
                            numInputs = 6,
                            isInputNum = true,
                            onlyNumbers = false,
                            inputLabel = 'token',
                            className = '',
                            required = false,
                            onChange,
                            emptyFields = false,
                            isValid = true
                        }) => {
    const { t } = useTranslation();
    const middleSeparatorIndex = Math.floor(numInputs / 2) - 1;

    const handleChange = (newValue) => {
        const filtered = onlyNumbers ? newValue.replace(/\D/g, '') : newValue;
        onChange?.(filtered);
    };

    return (
        <Box sx={((emptyFields && !value) || isValid === false)
            ? CustomStyleOtpContainerRequired
            : CustomStyleOtpContainer
        }>
            <div className={"col-12 d-flex justify-content-center align-items-center "}>
                <CustomInputLabel
                    id={'btn_open_door_otp'}
                    className={`${emptyFields === true ? "font-color-error-600" : "font-color-shades-950"} fs-16px`}
                >
                    {t(inputLabel)}{(required && ' *')}
                </CustomInputLabel>
            </div>

            <div className={"col-12 p-0 d-flex justify-content-between otp-inputs mt-3"}>
                <OtpInput
                    value={value}
                    isInputNum={isInputNum}
                    onChange={handleChange}
                    numInputs={numInputs}
                    containerStyle={"d-flex justify-content-center "}
                    renderSeparator={(index) =>
                        index === middleSeparatorIndex ? (
                            <span className="mx-2 fs-20px font-color-general-950 d-flex align-items-center">
                                &bull;
                            </span>
                        ) : (
                            <span className={"w-8px"} />
                        )
                    }
                    renderInput={(props, index) => (
                        <input
                            {...props}
                            id={`otp_input_${index}`}
                            className="otp-input"
                            inputMode={onlyNumbers ? 'numeric' : 'text'}
                            pattern={onlyNumbers ? '[0-9]*' : undefined}
                        />
                    )}
                />
            </div>

            {emptyFields && (value === '' || value == null) &&
            <FormHelperText className={className} sx={CustomStyleFormHelperText(false)} error={true}>
                {t('requiredField')}
            </FormHelperText>
            }

            {isValid === false && value &&
            <FormHelperText className={className} error={true} sx={CustomStyleFormHelperText(false)}>
                {t('code_invalid_or_expired_try_it_again')}
            </FormHelperText>
            }
        </Box>
    );
};

export default CustomOtpInput;
